/* eslint-disable array-bracket-newline, quotes */
import { useCallback, useEffect, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import { useBalance } from 'wagmi';
import { Web3Provider } from '@ethersproject/providers';
import { Contract } from '@ethersproject/contracts';
import { LimitOrdersProxy } from '@kattana/limit-orders';
import { helpers, Network } from '@kattana-io/common';
import BigNumber from 'bignumber.js';
import { calculateUSDValue } from '../../../../helpers/calculateUSDValue';
import { Direction } from '../../hooks';
import { actionGetLimitOrders } from '../../../../actions/limit_orders';
import { BASE_TOKEN, SUPPORTED_CHAINS } from '../../helpers/nativeCoins';
import { getPriceForPair } from './../../helpers/statistics';
import { authTokenSelector } from '../../../../selectors/auth';
import {
  getInitialLimitState,
  LimitActions,
  limitReducer,
  LimitTabs,
  LimitTypes,
} from '../../reducers/limitReducer';
import { LimitOrderAPI } from '../../../../store/createStore';
import { actionAppendTransaction } from '../../../../actions/transactions';
import { toIntermediateTransaction } from '../../../../helpers/intermediateTransactions';
import { truncateDecimals } from '../../../../helpers/truncate';
import { ILimitProps } from '.';

const MSG_CANT_CREATE = "Couldn't create a limit order";

/**
 * 0.1 is 10000
 * A pip is the smallest whole unit measurement of the difference between the bid and ask spread in a foreign exchange quote.
 * A pip equals 1/100 of 1%, or . 0001. Thus, the forex quote extends out to four decimal places.
 * @param slippage
 */
const convertSlippageToPips = (slippage: number): string =>
  String(Math.round(slippage * 100));

const useLimit = ({
  base,
  quote,
  meta,
  slippage,
  onSetBaseAmount,
  onSetQuoteAmount,
  onNotify,
  onWalletRefresh,
  chainId,
  account,
  tradesCount,
  sendTokens,
  ratesAddress,
  current,
}: ILimitProps) => {
  const [state, dispatch] = useReducer(
    limitReducer,
    { baseAmount: base.amount, quoteAmount: quote.amount, meta },
    getInitialLimitState
  );

  const token = useSelector(authTokenSelector);
  const globalDispatch = useDispatch();

  const network = Network.fromChain(current.chain);
  const isSameChain = chainId === network?.toDecimal();

  useEffect(() => {
    const price = getPriceForPair({ ticker: meta, quotePrice: true });
    const limitPrice = base.amount.isGreaterThan(0)
      ? quote.amount.div(base.amount).toNumber()
      : price;
    dispatch({
      type: LimitActions.SetCurrentPrice,
      payload: price,
    });
    dispatch({
      type: LimitActions.SetLimitPrice,
      payload: limitPrice,
    });
    dispatch({
      type: LimitActions.SetStopPrice,
      payload: state.isGrid ? price : limitPrice,
    });
    dispatch({
      type: LimitActions.SetGrid,
      payload: {
        initPrice: price,
        finalPrice: price,
        ordersNum: state.grid.ordersNum,
      },
    });
  }, [meta]);

  useEffect(() => {
    if (state.isGrid) {
      if (state.stopPrice > 0) {
        onSetBaseAmount(quote.amount.div(state.stopPrice));
      }
    } else {
      const updatedPrice = quote.amount.div(base.amount);
      if (updatedPrice.isFinite() && !updatedPrice.eq(state.limitPrice)) {
        dispatch({
          type: LimitActions.SetLimitPrice,
          payload: updatedPrice.toNumber(),
        });
      }
    }
  }, [quote.amount]);

  useEffect(() => {
    const updatedQuote = base.amount.multipliedBy(state.stopPrice);

    if (!updatedQuote.eq(quote.amount)) {
      onSetQuoteAmount(updatedQuote);
    }
  }, [base.amount, state.stopPrice]);

  useBalance({
    address: account as any,
    token: base.address == BASE_TOKEN ? '' : (base.address as any),
    chainId: network?.toDecimal(),
    watch: true,
    onSuccess(data) {
      const amount = new BigNumber(data.formatted);
      dispatch({
        payload: truncateDecimals(amount.toNumber(), 4),
        type: LimitActions.SetBaseBalance,
      });
      const baseUSDValue = calculateUSDValue(
        base.address,
        amount,
        ratesAddress
      );
      dispatch({
        payload: baseUSDValue,
        type: LimitActions.SetBaseUSDBalance,
      });
    },
  });

  useBalance({
    address: account as any,
    token: quote.address == BASE_TOKEN ? '' : (quote.address as any),
    chainId: network?.toDecimal(),
    watch: true,
    onSuccess(data) {
      const amount = new BigNumber(data.formatted);
      dispatch({
        payload: truncateDecimals(amount.toNumber(), 4),
        type: LimitActions.SetQuoteBalance,
      });
      const quoteUSDValue = calculateUSDValue(
        quote.address,
        amount,
        ratesAddress
      );
      dispatch({
        payload: quoteUSDValue,
        type: LimitActions.SetQuoteUSDBalance,
      });
    },
  });

  /**
   * Rebuild tooltip when we change proxy
   */
  useEffect(() => {
    setTimeout(ReactTooltip.rebuild, 0);
  }, [state.limitTab]);

  const setLimitOrders = useCallback(async () => {
    const chId = chainId || 1;

    if (!SUPPORTED_CHAINS.includes(chId) || !account) {
      dispatch({
        payload: LimitTabs.NotSupported,
        type: LimitActions.SetLimitTab,
      });
      return;
    }

    try {
      const lp = new LimitOrdersProxy(chId);
      console.log('limit orders: checking if wallet has proxy');
      const success = await lp.walletHasProxy(account);
      console.log('limit orders: result', success);
      if (success) {
        const lp = new LimitOrdersProxy(chId);
        const address = await lp.getWalletProxyAddress(account);
        if (address) {
          dispatch({
            payload: address,
            type: LimitActions.SetProxyWallet,
          });
        }
        dispatch({
          payload: LimitTabs.Available,
          type: LimitActions.SetLimitTab,
        });
      } else {
        dispatch({
          payload: LimitTabs.NotAvailable,
          type: LimitActions.SetLimitTab,
        });
      }
    } catch (e) {
      console.error('limit orders: error', e);
    }
  }, [account, chainId]);

  /**
   * Lets check if user has proxy
   */
  useEffect(() => {
    setLimitOrders();
  }, [state.limitTab, chainId, account, setLimitOrders]);

  /**
   * Do estimate call to fetch quote value
   */
  const onBaseUpdate = (input: number) => {
    if (state.direction != Direction.BaseQuote) {
      dispatch({
        payload: Direction.BaseQuote,
        type: LimitActions.SetDirection,
      });
    }

    const newInput = new BigNumber(input);
    if (!base.amount.eq(newInput)) {
      onSetBaseAmount(newInput);
    }
  };

  const onQuoteUpdate = (input: number) => {
    if (state.direction != Direction.QuoteBase) {
      dispatch({
        payload: Direction.QuoteBase,
        type: LimitActions.SetDirection,
      });
    }

    const newInput = new BigNumber(input);
    if (!quote.amount.eq(newInput)) {
      onSetQuoteAmount(new BigNumber(input));
    }
  };

  const onCreateOrder = async () => {
    let gt: boolean;
    if (state.isGrid) {
      gt = state.grid.finalPrice > state.grid.initPrice ? true : false;
    } else {
      gt = state.currentPrice < state.limitPrice ? true : false;
    }

    if (base.amount.eq(0)) {
      onNotify({
        kind: 'warning',
        message: 'Could not create limit order with zero input',
      });
      return;
    }

    const value = {
      src: base.address,
      dest: quote.address,
      pair: meta?.address,
      name: meta?.name,
      price: state.isGrid
        ? state.grid.initPrice.toFixed(8)
        : state.limitPrice.toFixed(8),
      price_usd: '0',
      gt,
      amount_in: base.amount.times(10 ** base.decimals).toFixed(),
      amount_out: quote.amount.times(10 ** quote.decimals).toFixed(),
      flipped: meta?.flipped,
      proxy: state.proxyWallet,
      chain: meta?.chain,
      exchange: meta?.exchange,
      trigger_type: state.limitMode,
      slippage: convertSlippageToPips(slippage),
      trades_count: state.isGrid ? state.grid.ordersNum : tradesCount,
      src_decimals: base.decimals,
      dest_decimals: quote.decimals,
      start_price: state.grid.initPrice.toFixed(8),
      start_price_usd: '0',
      end_price: state.grid.finalPrice.toFixed(8),
      end_price_usd: '0',
      execution_type: state.isGrid ? LimitTypes.GRID : LimitTypes.LIMIT,
    };

    const provider = new Web3Provider((window as any).ethereum);
    const signer = provider.getSigner();

    const payload = {
      address: account,
      payload: value,
      chain_id: chainId,
    };

    if (!account) {
      return;
    }

    const canCreate = await LimitOrderAPI.canCreate(account, token);

    if (!canCreate) {
      onNotify({ kind: 'warning', message: MSG_CANT_CREATE });
      return;
    }

    /**
     * Send coin
     */
    if (base.address.toLocaleLowerCase() !== BASE_TOKEN && sendTokens) {
      try {
        console.log('Sending erc20 coin to proxy wallet');
        const amount = base.amount.times(10 ** base.decimals).toFixed();
        console.log('Proxy contract amount to be deposited: ', amount);
        const contract = new Contract(base.address, helpers.ERC20Abi, signer);
        const transferRequest = await contract.populateTransaction.transfer(
          state.proxyWallet,
          amount
        );
        const transferTx = await signer.sendTransaction(transferRequest);
        globalDispatch(
          actionAppendTransaction(
            toIntermediateTransaction(transferTx.hash, chainId)
          )
        );
        console.log('Info: ', transferTx);
      } catch (e: any) {
        if (e.error.data.message) {
          onNotify({
            kind: 'error',
            message: 'Could not send token: ' + e.error.data.message,
          });
        }
        console.warn(e);
        return;
      }
    }

    let response: any;
    if (state.isGrid) {
      response = await LimitOrderAPI.createGrid(payload, token);
    } else {
      response = await LimitOrderAPI.create(payload, token);
    }

    if (response instanceof Error) {
      onNotify({ kind: 'warning', message: MSG_CANT_CREATE });
      return;
    } else {
      onNotify({
        kind: 'info',
        message: 'You have successfully placed a limit order',
      });
    }

    /**
     * Refresh limit orders and refetch balances for wallet
     */
    globalDispatch(actionGetLimitOrders({ wallet: account, token }));
    onWalletRefresh([base.address, quote.address]);
  };

  /**
   * Deploy function
   */
  const onDeployRequested = async () => {
    try {
      if (state.limitTab !== LimitTabs.Deploying) {
        dispatch({
          payload: LimitTabs.Deploying,
          type: LimitActions.SetLimitTab,
        });
        const chId = Number(chainId);
        const provider = new Web3Provider((window as any).ethereum);
        const signer = provider.getSigner();
        const lp = new LimitOrdersProxy(chId);
        const tx = await lp.createWalletProxy(account as any, signer);
        console.log('transaction object: ', tx);
        if (!tx) {
          onNotify({
            kind: 'error',
            message: 'Proxy contract needs to be deployed',
          });
          dispatch({
            payload: LimitTabs.NotSupported,
            type: LimitActions.SetLimitTab,
          });
        } else {
          const receipt = await tx.wait();
          console.log('transaction receipt: ', receipt);
          if (receipt) {
            dispatch({
              payload: LimitTabs.Available,
              type: LimitActions.SetLimitTab,
            });
            onNotify({
              kind: 'info',
              message: 'You have successfully deployed a proxy contract',
            });
          } else {
            dispatch({
              payload: LimitTabs.NotAvailable,
              type: LimitActions.SetLimitTab,
            });
          }
        }
      } else {
        console.log('deploying already in progress, skipping');
      }
    } catch (err) {
      dispatch({
        payload: LimitTabs.NotAvailable,
        type: LimitActions.SetLimitTab,
      });
      onNotify({ kind: 'error', message: 'Could not deploy a proxy contract' });
      console.error(err);
    }
  };

  const mergedState = {
    ...state,
    network,
    isSameChain,
    slippage,
    tradesCount,
    onBaseUpdate,
    onQuoteUpdate,
    onCreateOrder,
    onDeployRequested,
    dispatch,
  };

  return mergedState;
};

export default useLimit;
